import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Maximize2, Search, X } from 'lucide-react';
import { HistoricalBar, Position, tradingApi } from '../services/api';
import { isValidHistoricalBar } from '../market/historicalBars';
import ChartToolbarButton from './Chart/ChartToolbarButton';
import ChartFrame from './Chart/ChartFrame';
import { ChartStyleToggle, ChartToolbar, ChartToolbarSelect } from './Chart/ChartToolbar';
import { LightweightChart, type ChartLineOverlay } from './Chart/LightweightChart';
import ModalPortal from './ModalPortal';
import { themeColors } from '../styles/theme';

type ChartTimeframe = '1min' | '5min' | '15min' | '30min' | '1h' | '1d';
type ChartDuration = '1 D' | '5 D' | '2 W' | '1 M' | '3 M' | '6 M' | '1 Y';
type ChartStyle = 'candles' | 'line';
type MovingAverageKind = 'off' | 'sma' | 'ema';

interface ChartBar {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface MovingAverageConfig {
  period: number;
  kind: MovingAverageKind;
  color: string;
}

interface PositionChartModalProps {
  position: Position;
  onClose: () => void;
  inline?: boolean;
  onOpenModal?: () => void;
}

const timeframeOptions = [
  { value: '1min', label: '1 minute' },
  { value: '5min', label: '5 minutes' },
  { value: '15min', label: '15 minutes' },
  { value: '30min', label: '30 minutes' },
  { value: '1h', label: '1 hour' },
  { value: '1d', label: '1 day' },
];

const durationOptions = [
  { value: '1 D', label: '1 day' },
  { value: '5 D', label: '5 days' },
  { value: '2 W', label: '2 weeks' },
  { value: '1 M', label: '1 month' },
  { value: '3 M', label: '3 months' },
  { value: '6 M', label: '6 months' },
  { value: '1 Y', label: '1 year' },
];

const movingAverageOptions = [
  { value: 'off', label: 'Off' },
  { value: 'sma', label: 'SMA' },
  { value: 'ema', label: 'EMA' },
];

const movingAverageColors = [themeColors.neon, themeColors.warningBright, themeColors.info];

const chartBars = (bars: HistoricalBar[]): ChartBar[] => bars
  .filter(isValidHistoricalBar)
  .map((bar) => ({
    time: bar.time,
    open: bar.open,
    high: bar.high,
    low: bar.low,
    close: bar.close,
    volume: bar.volume ?? 0,
  }))
  .sort((a, b) => a.time - b.time);

const toIsoDate = (time: number) => new Date(time * 1000).toISOString();

const averageData = (bars: ChartBar[], period: number, kind: Exclude<MovingAverageKind, 'off'>) => {
  if (bars.length < period) return [];
  if (kind === 'sma') {
    const result: Array<{ date: string; value: number }> = [];
    let sum = 0;
    bars.forEach((bar, index) => {
      sum += bar.close;
      if (index >= period) sum -= bars[index - period].close;
      if (index >= period - 1) result.push({ date: toIsoDate(bar.time), value: sum / period });
    });
    return result;
  }

  const multiplier = 2 / (period + 1);
  const result: Array<{ date: string; value: number }> = [];
  let previous = bars.slice(0, period).reduce((sum, bar) => sum + bar.close, 0) / period;
  result.push({ date: toIsoDate(bars[period - 1].time), value: previous });
  bars.slice(period).forEach((bar) => {
    previous = (bar.close - previous) * multiplier + previous;
    result.push({ date: toIsoDate(bar.time), value: previous });
  });
  return result;
};

const defaultVisibleBars = (timeframe: ChartTimeframe) => {
  if (timeframe === '1d') return 180;
  if (timeframe === '1min') return 240;
  return 220;
};

const displaySymbol = (position: Position) =>
  position.display_symbol || position.local_symbol || position.symbol;

const getHistorySymbol = (position: Position) =>
  position.secType === 'FUT'
    ? position.local_symbol || position.display_symbol || position.symbol
    : position.symbol;

const PositionChartModal: React.FC<PositionChartModalProps> = ({
  position,
  onClose,
  inline = false,
  onOpenModal,
}) => {
  const [timeframe, setTimeframe] = useState<ChartTimeframe>('15min');
  const [duration, setDuration] = useState<ChartDuration>('1 M');
  const [chartStyle, setChartStyle] = useState<ChartStyle>('candles');
  const [ma20, setMa20] = useState<MovingAverageKind>('off');
  const [ma50, setMa50] = useState<MovingAverageKind>('off');
  const [ma200, setMa200] = useState<MovingAverageKind>('off');
  const [historySymbol, setHistorySymbol] = useState(() => getHistorySymbol(position));
  const [symbolInput, setSymbolInput] = useState(() => getHistorySymbol(position));
  const [bars, setBars] = useState<ChartBar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const positionIdentity = position.position_key || position.contract_key || `${position.secType || 'STK'}:${position.symbol}:${position.local_symbol || ''}`;
  const nextHistorySymbol = getHistorySymbol(position);

  useEffect(() => {
    setHistorySymbol(nextHistorySymbol);
    setSymbolInput(nextHistorySymbol);
  }, [nextHistorySymbol, positionIdentity]);

  useEffect(() => {
    if (inline) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [inline, onClose]);

  useEffect(() => {
    const controller = new AbortController();
    const loadHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await tradingApi.getHistoricalData(
          historySymbol,
          timeframe,
          duration,
          'ibkr',
          controller.signal,
        );
        if (!response.success || !Array.isArray(response.data)) {
          throw new Error(response.error || 'No historical data returned');
        }
        if (!controller.signal.aborted) setBars(chartBars(response.data));
      } catch (err) {
        if (!controller.signal.aborted) {
          setBars([]);
          setError(err instanceof Error ? err.message : 'Failed to load chart');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadHistory();
    return () => controller.abort();
  }, [historySymbol, timeframe, duration]);

  const chartData = useMemo(() => bars.map((bar) => ({
    date: toIsoDate(bar.time),
    open: bar.open,
    high: bar.high,
    low: bar.low,
    close: bar.close,
    volume: bar.volume,
  })), [bars]);

  const lineOverlays = useMemo<ChartLineOverlay[]>(() => {
    const configs: MovingAverageConfig[] = [
      { period: 20, kind: ma20, color: movingAverageColors[0] },
      { period: 50, kind: ma50, color: movingAverageColors[1] },
      { period: 200, kind: ma200, color: movingAverageColors[2] },
    ];
    return configs.flatMap((config) => {
      if (config.kind === 'off') return [];
      const data = averageData(bars, config.period, config.kind);
      return data.length ? [{
        title: `${config.kind.toUpperCase()} ${config.period}`,
        color: config.color,
        lineWidth: 2 as const,
        data,
      }] : [];
    });
  }, [bars, ma20, ma50, ma200]);

  const volumeData = useMemo(() => bars.map((bar) => ({
    date: toIsoDate(bar.time),
    value: bar.volume,
    color: bar.close >= bar.open ? themeColors.success : themeColors.danger,
  })), [bars]);

  const loadChartSymbol = () => {
    const nextSymbol = symbolInput.trim().toUpperCase();
    if (!nextSymbol || nextSymbol === historySymbol) return;
    setHistorySymbol(nextSymbol);
  };

  const handleSymbolInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      loadChartSymbol();
    }
  };

  const maSelect = (period: number, value: MovingAverageKind, onChange: (next: string) => void) => (
    <div className="flex shrink-0 items-center gap-1">
      <span className="text-[9px] font-bold uppercase tracking-wide text-muted">MA{period}</span>
      <ChartToolbarSelect
        value={value}
        options={movingAverageOptions}
        onChange={onChange}
        ariaLabel={`Moving average ${period}`}
        title={`Moving average ${period}`}
        widthClassName="w-[58px]"
      />
    </div>
  );

  const chartToolbar = (
    <ChartToolbar
      leading={(
        <>
          <div className="flex shrink-0 items-center gap-1.5">
            <h2 className="text-xs font-bold tracking-tight text-white">{displaySymbol(position)}</h2>
            <span className="rounded-sm border border-neon/20 bg-neon/10 px-1 py-0.5 text-[8px] font-bold uppercase tracking-wider text-neon">
              {position.secType || 'STK'}
            </span>
          </div>
          <div className="flex h-7 w-[128px] shrink-0 overflow-hidden rounded-md border border-white/10 bg-black/20 focus-within:border-neon/50">
            <input
              value={symbolInput}
              onChange={(event) => setSymbolInput(event.target.value.toUpperCase())}
              onKeyDown={handleSymbolInputKeyDown}
              aria-label="Chart symbol"
              className="min-w-0 flex-1 bg-transparent px-2 text-[10px] font-bold text-white outline-hidden placeholder:text-white/25"
              placeholder="Underlying stock"
            />
            <div className="flex items-center border-l border-white/10">
              <ChartToolbarButton
                size="compact"
                iconOnly
                onClick={loadChartSymbol}
                icon={<Search className="h-3.5 w-3.5" />}
                label="Load chart symbol"
              />
            </div>
          </div>
        </>
      )}
      chartStyle={<ChartStyleToggle value={chartStyle} onChange={setChartStyle} />}
      settings={(
        <>
          <ChartToolbarSelect
            value={duration}
            options={durationOptions}
            onChange={(value) => setDuration(value as ChartDuration)}
            ariaLabel="Chart lookback"
            title="Lookback"
            widthClassName="w-[86px]"
          />
          {maSelect(20, ma20, (value) => setMa20(value as MovingAverageKind))}
          {maSelect(50, ma50, (value) => setMa50(value as MovingAverageKind))}
          {maSelect(200, ma200, (value) => setMa200(value as MovingAverageKind))}
        </>
      )}
      trailing={(
        <>
          <ChartToolbarSelect
            value={timeframe}
            options={timeframeOptions}
            onChange={(value) => setTimeframe(value as ChartTimeframe)}
            ariaLabel="Chart bar interval"
            title="Bar interval"
            widthClassName="w-[102px]"
          />
          {inline && onOpenModal && (
            <ChartToolbarButton
              size="compact"
              iconOnly
              onClick={onOpenModal}
              icon={<Maximize2 className="h-3.5 w-3.5" />}
              label="Open position chart in modal"
              title="Open full chart"
            />
          )}
          {!inline && (
            <ChartToolbarButton
              size="compact"
              iconOnly
              onClick={onClose}
              icon={<X className="h-3.5 w-3.5" />}
              label="Close position chart"
            />
          )}
        </>
      )}
    />
  );

  const chartPlot = (
    <div className={`relative min-h-0 flex-1 ${inline ? 'h-[320px] min-h-[280px]' : 'min-h-[320px]'} p-1`}>
      <LightweightChart
        key={`${positionIdentity}:${historySymbol}:${timeframe}:${duration}`}
        data={chartData}
        volumeData={volumeData}
        lineOverlays={lineOverlays}
        type="symbol"
        symbolStyle={chartStyle}
        lineColor={themeColors.neon}
        resolution={timeframe === '1d' ? '1D' : 'native'}
        timeVisible={timeframe !== '1d'}
        initialVisibleBars={defaultVisibleBars(timeframe)}
        latestVisibleBars={defaultVisibleBars(timeframe)}
        showLatestControl
        hideControls
      />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#0e0e12]/65">
          <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-xs text-muted">
            <Loader2 className="h-4 w-4 animate-spin text-neon" /> Loading history…
          </div>
        </div>
      )}
      {!loading && error && (
        <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-rose-300">
          {error}
        </div>
      )}
      {!loading && !error && bars.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-muted">
          No historical bars available for {historySymbol}.
        </div>
      )}
    </div>
  );

  if (inline) {
    return (
      <ChartFrame className="w-full border-t border-white/10" toolbar={chartToolbar}>
        {chartPlot}
      </ChartFrame>
    );
  }

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-1000 flex items-center justify-center bg-black/85 p-4 backdrop-blur-md"
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <div className="h-[min(94vh,900px)] w-full max-w-[1400px] overflow-hidden rounded-2xl border border-white/10 bg-[#0e0e12] shadow-[0_0_60px_rgba(0,0,0,0.55)]">
          <ChartFrame className="h-full" toolbar={chartToolbar}>
            {chartPlot}
          </ChartFrame>
        </div>
      </div>
    </ModalPortal>
  );
};

export default PositionChartModal;
